/**
 * ApprovalsConfig — Manager+ tab.
 *
 * Per Bill 2026-04-24: the "config + reporting only" copy from the original
 * RAP module brief was wrong about the action surface (we now do live
 * approve/reject inline), but the config + reporting *content* is still
 * relevant — KEEP it and add to it.
 *
 * Fields:
 *   - Approval reason templates (canned justifications the approver can pick)
 *   - Auto-approve thresholds (low-risk requests skip the approver entirely)
 *   - Latency-SLA targets (when does an approval become "urgent")
 *   - CD waiver authority matrix (existing)
 *   - Cart archive reasons requiring approval (existing)
 *
 * Edits route through `/v3/admin/config`. Read-only here per the existing
 * pattern; the Admin module owns the write path.
 */

import { useConfig } from '@/contexts/ConfigContext';
import { Pill } from '@/components/primitives';

const ARCHIVE_REASONS = ['abandoned', 'cancelled', 'committed', 'adverse', 'fraud', 'duplicate'];

interface ApprovalsConfigProps {
  /** When true, show all sections (Manager+). Otherwise hide the matrix and thresholds. */
  managerView?: boolean;
}

const DEFAULT_REASON_TEMPLATES: ReadonlyArray<{ key: string; label: string; comment: string }> = [
  {
    key: 'rejection-incomplete',
    label: 'Rejection — incomplete documentation',
    comment: 'Please re-submit with the missing supporting documentation attached.',
  },
  {
    key: 'rejection-policy',
    label: 'Rejection — outside policy',
    comment: 'Request falls outside current policy thresholds. Escalate via Compliance.',
  },
  {
    key: 'approval-standard',
    label: 'Approval — standard',
    comment: 'Approved per standard authority matrix.',
  },
  {
    key: 'approval-exception',
    label: 'Approval — exception',
    comment: 'Approved as a one-time exception. See attached file note.',
  },
];

const DEFAULT_AUTO_APPROVE_THRESHOLDS: ReadonlyArray<{ category: string; ceiling: string; help: string }> = [
  {
    category: 'Wire — domestic',
    ceiling: '$5,000',
    help: 'Domestic wires below this amount can auto-approve when the requester has full signing authority.',
  },
  {
    category: 'CD penalty waiver',
    ceiling: '$100',
    help: 'Penalty waivers under this amount auto-approve (officer-level authority).',
  },
  {
    category: 'Cart archive — abandoned',
    ceiling: 'always',
    help: 'Abandoned carts archive without supervisor approval (audit log only).',
  },
];

const DEFAULT_SLA = {
  pendingMinutesBeforeUrgent: 60,
  pendingMinutesBeforeBreach: 240,
};

export function ApprovalsConfig({ managerView = true }: ApprovalsConfigProps): JSX.Element {
  const { config } = useConfig();
  const archiveSetting =
    (config.systemSettings?.['CartArchiveRequiresApproval'] as Record<string, boolean> | undefined) ?? {};

  return (
    <div>
      <p
        style={{
          marginTop: 0,
          marginBottom: 'var(--sp-6)',
          fontSize: 12,
          color: 'var(--ink-600)',
          lineHeight: 1.5,
        }}
      >
        Read-only view — edits route through <code>/v3/admin/config</code>. Each field has inline
        help describing the impact on approvers and requesters.
      </p>

      {/* ---------------- Approval reason templates ---------------- */}
      <section className="rap-config-section">
        <h3 className="rap-config-section__title">Approval reason templates</h3>
        <p className="rap-config-section__help">
          Canned justifications shown in the Approve / Reject modal. Approvers can pick one with a
          click instead of free-typing — speeds decisions and standardizes the audit trail.
        </p>
        {DEFAULT_REASON_TEMPLATES.map((t) => (
          <div key={t.key} className="rap-config-row">
            <div className="rap-config-row__label">
              {t.label}
              <span className="rap-config-row__help">
                Inserted into the comment box when the approver picks this template.
              </span>
            </div>
            <div className="rap-config-row__control">
              <textarea defaultValue={t.comment} readOnly rows={2} />
            </div>
          </div>
        ))}
      </section>

      {/* ---------------- Auto-approve thresholds ---------------- */}
      {managerView && (
        <section className="rap-config-section">
          <h3 className="rap-config-section__title">Auto-approve thresholds</h3>
          <p className="rap-config-section__help">
            Requests at or below the ceiling skip the approver entirely and are recorded as
            auto-approved in the audit log. Use sparingly — every threshold here is a control gap if
            misconfigured.
          </p>
          {DEFAULT_AUTO_APPROVE_THRESHOLDS.map((t) => (
            <div key={t.category} className="rap-config-row">
              <div className="rap-config-row__label">
                {t.category}
                <span className="rap-config-row__help">{t.help}</span>
              </div>
              <div className="rap-config-row__control">
                <input type="text" defaultValue={t.ceiling} readOnly />
              </div>
            </div>
          ))}
        </section>
      )}

      {/* ---------------- SLA targets ---------------- */}
      {managerView && (
        <section className="rap-config-section">
          <h3 className="rap-config-section__title">Latency SLA targets</h3>
          <p className="rap-config-section__help">
            When an approval has been pending longer than the urgency threshold the rail badge
            flashes red. Past the breach threshold the request is escalated via the notifications
            spine.
          </p>
          <div className="rap-config-row">
            <div className="rap-config-row__label">
              Mark as urgent after
              <span className="rap-config-row__help">
                Minutes pending before the card border + rail badge flip to red.
              </span>
            </div>
            <div className="rap-config-row__control">
              <input
                type="text"
                defaultValue={`${DEFAULT_SLA.pendingMinutesBeforeUrgent} minutes`}
                readOnly
              />
            </div>
          </div>
          <div className="rap-config-row">
            <div className="rap-config-row__label">
              SLA breach escalation after
              <span className="rap-config-row__help">
                Minutes pending before the requester's manager is notified via the notifications spine.
              </span>
            </div>
            <div className="rap-config-row__control">
              <input
                type="text"
                defaultValue={`${DEFAULT_SLA.pendingMinutesBeforeBreach} minutes`}
                readOnly
              />
            </div>
          </div>
        </section>
      )}

      {/* ---------------- Existing: cart archive reasons ---------------- */}
      <section className="rap-config-section">
        <h3 className="rap-config-section__title">Cart archive reasons requiring Supervisor approval</h3>
        <p className="rap-config-section__help">
          When a cart is archived under one of these reasons, a Teams Approval is generated and
          must be decided before the cart finalizes. Other reasons archive without approval.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--ink-50)', textAlign: 'left' }}>
              <th style={{ padding: 'var(--sp-3)' }}>Reason</th>
              <th style={{ padding: 'var(--sp-3)' }}>Requires approval?</th>
            </tr>
          </thead>
          <tbody>
            {ARCHIVE_REASONS.map((r) => (
              <tr key={r} style={{ borderTop: '1px solid var(--ink-200)' }}>
                <td style={{ padding: 'var(--sp-3)' }}>{r}</td>
                <td style={{ padding: 'var(--sp-3)' }}>
                  {archiveSetting[r] === true ? (
                    <Pill variant="warn">Yes</Pill>
                  ) : (
                    <Pill variant="muted">No</Pill>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ---------------- Existing: CD waiver authority matrix ---------------- */}
      {managerView && (
        <section className="rap-config-section">
          <h3 className="rap-config-section__title">CD waiver authority matrix</h3>
          <p className="rap-config-section__help">
            Drives which roles can grant a CD penalty waiver up to a given dollar threshold. When
            the request exceeds the highest available role's ceiling, an approval is generated for
            the next role up.
          </p>
          {config.signatureAuthRules.length === 0 ? (
            <div style={{ color: 'var(--ink-600)', fontSize: 13 }}>
              No signature auth rules loaded (config route may have 503'd).
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--ink-50)', textAlign: 'left' }}>
                  <th style={{ padding: 'var(--sp-3)' }}>Rule</th>
                  <th style={{ padding: 'var(--sp-3)' }}>Category</th>
                  <th style={{ padding: 'var(--sp-3)' }}>Required roles</th>
                  <th style={{ padding: 'var(--sp-3)', textAlign: 'right' }}>$ threshold</th>
                </tr>
              </thead>
              <tbody>
                {config.signatureAuthRules.map((rule) => (
                  <tr key={rule.ruleId} style={{ borderTop: '1px solid var(--ink-200)' }}>
                    <td style={{ padding: 'var(--sp-3)' }}>{rule.ruleName}</td>
                    <td style={{ padding: 'var(--sp-3)' }}>{rule.transactionCategory ?? '—'}</td>
                    <td style={{ padding: 'var(--sp-3)' }}>{rule.requiredRoles.join(', ')}</td>
                    <td
                      style={{
                        padding: 'var(--sp-3)',
                        textAlign: 'right',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      {rule.dollarThreshold}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
